import axios from 'axios';

import { PLAN_BOATS as GENERATED_PLAN_BOATS } from '../data/subscriptionPlanCatalog';

export type BillingPeriod = 'day' | 'month' | 'year';
export type PlanPromptReason = 'new-user' | 'missing-plan' | 'expired' | 'change-plan';
export type SubscriptionStatusValue =
  | 'none'
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused'
  | 'expired'
  | 'unlimited';

export interface PlanBoat {
  id: string;
  name: string;
  tier: 'One Design' | 'Performance' | 'Grand Prix';
  category?: string;
  tags?: string[];
  prices: {
    day: number;
    month: number;
    year: number;
  };
}

export interface StoredSubscription {
  boatId: string;
  billingPeriod: BillingPeriod;
  status: Exclude<SubscriptionStatusValue, 'none'>;
  trialStartedAt: string;
  trialEndsAt: string;
  selectedAt: string;
}

export interface SubscriptionSnapshot {
  status: SubscriptionStatusValue;
  subscription: StoredSubscription | null;
  isUsable: boolean;
  isUnlimited: boolean;
  daysLeft: number;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
}

export interface StripeCheckoutPayload {
  boatId: string;
  billingPeriod: BillingPeriod;
  trialDays: number;
}

interface BackendSubscription {
  status: SubscriptionStatusValue;
  isUsable: boolean;
  isUnlimited: boolean;
  boatId: string | null;
  billingPeriod: BillingPeriod | null;
  trialStartsAt: string | null;
  trialEndsAt: string | null;
  currentPeriodEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  updatedAt: string | null;
}

interface SubscriptionStatusResponse {
  success: boolean;
  subscription: BackendSubscription;
}

interface SubscriptionMutationResponse {
  success: boolean;
  subscription: BackendSubscription;
}

interface StripeRedirectResponse {
  success?: boolean;
  url?: string;
  message?: string;
}

const getBackendErrorMessage = (error: unknown): string | null => {
  const data = axios.isAxiosError(error) ? error.response?.data : null;
  if (!data || typeof data !== 'object' || !('message' in data)) {
    return null;
  }
  const message = (data as { message?: unknown }).message;
  if (typeof message !== 'string' || !message.trim()) {
    return null;
  }
  if (message.includes('STRIPE_SECRET_KEY')) {
    return 'La conexion con Stripe no esta configurada en este entorno. Anade la clave de prueba de Stripe en el backend para continuar.';
  }
  return message;
};

const throwBackendAwareError = (error: unknown): never => {
  const backendMessage = getBackendErrorMessage(error);
  if (backendMessage) {
    throw new Error(backendMessage);
  }
  throw error;
};

export const TRIAL_DAYS = 14;
export const SUBSCRIPTION_STORAGE_KEY = 'sailtrackSubscription';
export const PLAN_PROMPT_REASON_KEY = 'sailtrackPlanPromptReason';
export const PENDING_CHECKOUT_SELECTION_KEY = 'sailtrackPendingCheckoutSelection';
export const DEFAULT_PLAN_BOAT_ID = 'j70';

export const PLAN_BOATS: PlanBoat[] = GENERATED_PLAN_BOATS as PlanBoat[];
const DEFAULT_PLAN_BOAT = PLAN_BOATS.find((boat) => boat.id === DEFAULT_PLAN_BOAT_ID) ?? PLAN_BOATS[0];

const API_BASE_URL: string = process.env.REACT_APP_API_BASE_URL ?? '';

const isBillingPeriod = (value: unknown): value is BillingPeriod =>
  value === 'day' || value === 'month' || value === 'year';

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

export const findPlanBoat = (boatId: string): PlanBoat =>
  PLAN_BOATS.find((boat) => boat.id === boatId) ?? DEFAULT_PLAN_BOAT;

export const getPlanPromptReason = (): PlanPromptReason | null => {
  const value = localStorage.getItem(PLAN_PROMPT_REASON_KEY);
  if (
    value === 'new-user' ||
    value === 'missing-plan' ||
    value === 'expired' ||
    value === 'change-plan'
  ) {
    return value;
  }
  return null;
};

export const setPlanPromptReason = (reason: PlanPromptReason): void => {
  localStorage.setItem(PLAN_PROMPT_REASON_KEY, reason);
};

export const clearPlanPromptReason = (): void => {
  localStorage.removeItem(PLAN_PROMPT_REASON_KEY);
};

export const storePendingCheckoutSelection = (
  boatId: string,
  billingPeriod: BillingPeriod,
): void => {
  localStorage.setItem(
    PENDING_CHECKOUT_SELECTION_KEY,
    JSON.stringify({ boatId, billingPeriod, selectedAt: new Date().toISOString() }),
  );
};

export const getPendingCheckoutSelection = (): Pick<
  StoredSubscription,
  'boatId' | 'billingPeriod' | 'selectedAt'
> | null => {
  const raw = localStorage.getItem(PENDING_CHECKOUT_SELECTION_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredSubscription>;
    if (
      typeof parsed.boatId !== 'string' ||
      !isBillingPeriod(parsed.billingPeriod) ||
      typeof parsed.selectedAt !== 'string'
    ) {
      return null;
    }
    return {
      boatId: parsed.boatId,
      billingPeriod: parsed.billingPeriod,
      selectedAt: parsed.selectedAt,
    };
  } catch {
    return null;
  }
};

export const clearPendingCheckoutSelection = (): void => {
  localStorage.removeItem(PENDING_CHECKOUT_SELECTION_KEY);
};

export const getStoredSubscription = (): StoredSubscription | null => {
  const raw = localStorage.getItem(SUBSCRIPTION_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredSubscription>;
    if (
      typeof parsed.boatId !== 'string' ||
      !isBillingPeriod(parsed.billingPeriod) ||
      (parsed.status !== 'trialing' &&
        parsed.status !== 'active' &&
        parsed.status !== 'expired' &&
        parsed.status !== 'unlimited') ||
      typeof parsed.trialStartedAt !== 'string' ||
      typeof parsed.trialEndsAt !== 'string' ||
      typeof parsed.selectedAt !== 'string'
    ) {
      return null;
    }
    return parsed as StoredSubscription;
  } catch {
    return null;
  }
};

export const getSubscriptionSnapshot = (): SubscriptionSnapshot => {
  const subscription = getStoredSubscription();
  if (!subscription) {
    return {
      status: 'none',
      subscription: null,
      isUsable: false,
      isUnlimited: false,
      daysLeft: 0,
      stripeCustomerId: null,
      stripeSubscriptionId: null,
    };
  }

  if (subscription.status === 'expired') {
    return {
      status: 'expired',
      subscription,
      isUsable: false,
      isUnlimited: false,
      daysLeft: 0,
      stripeCustomerId: null,
      stripeSubscriptionId: null,
    };
  }

  if (subscription.status === 'active' || subscription.status === 'unlimited') {
    return {
      status: subscription.status,
      subscription,
      isUsable: true,
      isUnlimited: subscription.status === 'unlimited',
      daysLeft: 0,
      stripeCustomerId: null,
      stripeSubscriptionId: null,
    };
  }

  const trialEndsAt = new Date(subscription.trialEndsAt).getTime();
  const now = Date.now();
  if (!Number.isFinite(trialEndsAt) || trialEndsAt <= now) {
    const expiredSubscription: StoredSubscription = { ...subscription, status: 'expired' };
    localStorage.setItem(SUBSCRIPTION_STORAGE_KEY, JSON.stringify(expiredSubscription));
    return {
      status: 'expired',
      subscription: expiredSubscription,
      isUsable: false,
      isUnlimited: false,
      daysLeft: 0,
      stripeCustomerId: null,
      stripeSubscriptionId: null,
    };
  }

  const daysLeft = Math.max(1, Math.ceil((trialEndsAt - now) / (1000 * 60 * 60 * 24)));
  return {
    status: 'trialing',
    subscription,
    isUsable: true,
    isUnlimited: false,
    daysLeft,
    stripeCustomerId: null,
    stripeSubscriptionId: null,
  };
};

export const createTrialSubscription = (
  boatId: string,
  billingPeriod: BillingPeriod,
): StoredSubscription => {
  const now = new Date();
  const subscription: StoredSubscription = {
    boatId,
    billingPeriod,
    status: 'trialing',
    trialStartedAt: now.toISOString(),
    trialEndsAt: addDays(now, TRIAL_DAYS).toISOString(),
    selectedAt: now.toISOString(),
  };

  localStorage.setItem(SUBSCRIPTION_STORAGE_KEY, JSON.stringify(subscription));
  return subscription;
};

export const activateTrialFromSuccessfulCheckout = (): StoredSubscription | null => {
  const pendingSelection = getPendingCheckoutSelection();
  if (!pendingSelection) return null;

  const subscription = createTrialSubscription(
    pendingSelection.boatId,
    pendingSelection.billingPeriod,
  );
  clearPendingCheckoutSelection();
  clearPlanPromptReason();
  return subscription;
};

const backendSubscriptionToSnapshot = (
  backendSubscription: BackendSubscription,
): SubscriptionSnapshot => {
  const hasPlanSelection = backendSubscription.boatId && backendSubscription.billingPeriod;
  const subscription: StoredSubscription | null = hasPlanSelection
    ? {
        boatId: backendSubscription.boatId as string,
        billingPeriod: backendSubscription.billingPeriod as BillingPeriod,
        status:
          backendSubscription.status === 'unlimited'
            ? 'unlimited'
            : backendSubscription.status === 'active'
              ? 'active'
              : backendSubscription.status === 'trialing'
                ? 'trialing'
                : 'expired',
        trialStartedAt: backendSubscription.trialStartsAt ?? backendSubscription.updatedAt ?? '',
        trialEndsAt: backendSubscription.trialEndsAt ?? backendSubscription.currentPeriodEndsAt ?? '',
        selectedAt: backendSubscription.updatedAt ?? '',
      }
    : null;
  const trialEndsAt = backendSubscription.trialEndsAt
    ? new Date(backendSubscription.trialEndsAt).getTime()
    : Number.NaN;
  const daysLeft =
    backendSubscription.status === 'trialing' && Number.isFinite(trialEndsAt)
      ? Math.max(1, Math.ceil((trialEndsAt - Date.now()) / (1000 * 60 * 60 * 24)))
      : 0;

  return {
    status: backendSubscription.status,
    subscription,
    isUsable: backendSubscription.isUsable,
    isUnlimited: backendSubscription.isUnlimited,
    daysLeft,
    stripeCustomerId: backendSubscription.stripeCustomerId,
    stripeSubscriptionId: backendSubscription.stripeSubscriptionId,
  };
};

export const fetchSubscriptionSnapshot = async (): Promise<SubscriptionSnapshot> => {
  const localSnapshot = getSubscriptionSnapshot();
  if (!API_BASE_URL) {
    return localSnapshot;
  }

  const token = localStorage.getItem('token');
  if (!token) {
    return localSnapshot;
  }

  const response = await axios.get<SubscriptionStatusResponse>(
    `${API_BASE_URL}/api/payments/subscription-status`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  const backendSnapshot = backendSubscriptionToSnapshot(response.data.subscription);
  if (!backendSnapshot.isUsable) {
    localStorage.removeItem(SUBSCRIPTION_STORAGE_KEY);
  }
  return backendSnapshot;
};

export const createStripeCheckoutSession = async (
  payload: StripeCheckoutPayload,
): Promise<string> => {
  if (!API_BASE_URL) {
    throw new Error('Stripe checkout endpoint is not configured.');
  }

  const token = localStorage.getItem('token');
  const response = await axios
    .post<StripeRedirectResponse>(
      `${API_BASE_URL}/api/payments/create-checkout-session`,
      payload,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      },
    )
    .catch(throwBackendAwareError);

  if (!response.data.url) {
    throw new Error('Stripe checkout did not return a redirect URL.');
  }

  storePendingCheckoutSelection(payload.boatId, payload.billingPeriod);
  return response.data.url;
};

export const changeStripeSubscriptionPlan = async (
  payload: StripeCheckoutPayload,
): Promise<SubscriptionSnapshot> => {
  if (!API_BASE_URL) {
    throw new Error('Stripe subscription endpoint is not configured.');
  }

  const token = localStorage.getItem('token');
  const response = await axios
    .post<SubscriptionMutationResponse>(
      `${API_BASE_URL}/api/payments/change-subscription-plan`,
      payload,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      },
    )
    .catch(throwBackendAwareError);

  return backendSubscriptionToSnapshot(response.data.subscription);
};

export const createStripePortalSession = async (
  returnPath = '/settings/payments',
): Promise<string> => {
  if (!API_BASE_URL) {
    throw new Error('Stripe portal endpoint is not configured.');
  }

  const token = localStorage.getItem('token');
  const response = await axios
    .post<StripeRedirectResponse>(
      `${API_BASE_URL}/api/payments/create-portal-session`,
      { returnPath },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      },
    )
    .catch(throwBackendAwareError);

  if (!response.data.url) {
    throw new Error('Stripe portal did not return a redirect URL.');
  }

  return response.data.url;
};
